import { useQuery } from '@apollo/client';
import { IonCol, IonGrid, IonRow } from '@ionic/react';
import equal from 'deep-equal';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { createEnumParam, StringParam, useQueryParams, withDefault } from 'use-query-params';

import AsyncSelect, { Option } from '@components/fields/AsyncSelect';
import RadioButtonGroup from '@components/fields/RadioButtonGroup';
import Select from '@components/fields/Select';
import Form from '@components/forms/Form';
import HandleQuery from '@components/HandleQuery';
import useAsyncSearch from '@hooks/useAsyncSearch';
import useLazyQueryWithPromise from '@hooks/useLazyQueryWithPromise';
import useMyExpert from '@hooks/useMyExpert';
import useNodesFromConnection from '@hooks/useNodesFromConnection';
import {
  ActivityActorKindEnum,
  ActivityKindEnum,
  ActivitySectionEnum,
  ActivitySummary,
  ExpertGlobalRoleEnum,
  GetExpertClientsDocument,
  GetExpertClientsQuery,
  GetExpertClientsQueryVariables,
  GetExpertsBasicDocument,
  GetExpertsBasicQuery,
  GetExpertsBasicQueryVariables,
  GetJourneyWithClientRolesQuery
} from '@typing/Generated';

type Client = NonNullable<NonNullable<GetExpertClientsQuery['expertClients']['edges']>[number]>['node'];

export enum OtherActivityKinds {
  ALL = 'all',
  ASSESSMENT_COMPLETED = 'assessment_completed',
  CONTENT_VIEWED = 'content_viewed'
}

export enum StartTimeValues {
  ALL = 'all',
  PAST_THREE_DAYS = 'past_three_days',
  PAST_SEVEN_DAYS = 'past_seven_days',
  PAST_THIRTY_DAYS = 'past_thirty_days'
}

type FormData = {
  activityKind?: ActivityKindEnum | OtherActivityKinds | null;
  actorKind?: ActivityActorKindEnum | null;
  clientId?: string | null;
  expertId?: string | null;
  section?: ActivitySectionEnum | null;
  startTime?: StartTimeValues | null;
};

type Props = {
  activitySummary?: ActivitySummary;
  clientRoles?: GetJourneyWithClientRolesQuery['journey']['clientRoles'][number][];
  filters: FormData;
  inDashboard: boolean;
  onChange: (filterValues: FormData) => void;
};

const ActivityFilters = ({ activitySummary, clientRoles, filters, inDashboard, onChange }: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const activityKindParam = createEnumParam<ActivityKindEnum | OtherActivityKinds>([
    ...Object.values(ActivityKindEnum),
    ...Object.values(OtherActivityKinds)
  ]);

  const actorKindParam = createEnumParam<ActivityActorKindEnum>(Object.values(ActivityActorKindEnum));

  const startTimeParam = createEnumParam<StartTimeValues>(Object.values(StartTimeValues));

  const sectionParam = createEnumParam<ActivitySectionEnum>(Object.values(ActivitySectionEnum));

  const [query, setQuery] = useQueryParams({
    activityKind: withDefault({ ...activityKindParam, urlName: 'activityKind' }, filters.activityKind),
    actorKind: withDefault({ ...actorKindParam, urlName: 'activityActorKind' }, filters.actorKind),
    clientId: withDefault({ ...StringParam, urlName: 'activityClientId' }, filters.clientId),
    clientName: { ...StringParam, urlName: 'activityClientName' },
    expertId: withDefault({ ...StringParam, urlName: 'activityExpertId' }, filters.expertId),
    section: withDefault({ ...sectionParam, urlName: 'activitySection' }, filters.section),
    startTime: withDefault({ ...startTimeParam, urlName: 'activityStartTime' }, filters.startTime)
  });

  const defaultValues: FormData = query;

  const getExpertClientsQuery = useLazyQueryWithPromise<GetExpertClientsQuery, GetExpertClientsQueryVariables>(
    GetExpertClientsDocument
  );

  const expertClientsAsyncSearch = useAsyncSearch<Client>({
    defaultOptions: [{ label: intl.formatMessage({ id: 'dictionary.any' }), value: '' }],
    getLabel: t => t?.fullName ?? '',
    getValue: t => t?.id ?? '',
    initialOption: { label: query.clientName, value: query.clientId }
  });

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange'
  });

  const getExpertsBasicQuery = useQuery<GetExpertsBasicQuery, GetExpertsBasicQueryVariables>(GetExpertsBasicDocument);

  const experts = useNodesFromConnection(getExpertsBasicQuery.data?.experts).filter(
    expert => expert.id !== myExpert?.id
  );

  const clientId = form.watch('clientId');
  const expertId = form.watch('expertId');
  const section = form.watch('section');
  const startTime = form.watch('startTime');
  const activityKind = form.watch('activityKind');
  const actorKind = form.watch('actorKind');

  useEffect(() => {
    const values = { activityKind, actorKind, clientId, expertId, section, startTime };
    if (!equal(values, filters)) {
      setQuery(values);
      onChange(values);
    }
  }, [activityKind, actorKind, clientId, expertId, filters, onChange, section, setQuery, startTime]);

  const startTimeOptions = useMemo(() => {
    const options = [StartTimeValues.PAST_SEVEN_DAYS, StartTimeValues.PAST_THIRTY_DAYS, StartTimeValues.ALL];

    return options.map(option => ({
      label: intl.formatMessage({ id: `models.activity.startTime.${option}` }),
      value: option
    }));
  }, [intl]);

  const activityKindOptions = useMemo(() => {
    const options = [
      { count: activitySummary?.activeJourneys ?? 0, value: OtherActivityKinds.ALL },
      { count: activitySummary?.consultsScheduled ?? 0, value: ActivityKindEnum.consult_scheduled },
      { count: activitySummary?.assessmentsCompleted ?? 0, value: OtherActivityKinds.ASSESSMENT_COMPLETED },
      { count: activitySummary?.contentViewed ?? 0, value: OtherActivityKinds.CONTENT_VIEWED }
    ];
    return options.map(option => ({
      label: intl.formatMessage({ id: `models.activity.activityKind.${option.value}` }, { count: option.count }),
      value: option.value
    }));
  }, [activitySummary, intl]);

  const actorKindOptions = useMemo(
    () => [
      { label: intl.formatMessage({ id: 'models.activity.actorKind.all' }), value: ActivityActorKindEnum.all },
      {
        label: intl.formatMessage({ id: 'models.activity.actorKind.clients' }),
        value: ActivityActorKindEnum.clients
      },
      {
        label: intl.formatMessage({ id: 'models.activity.actorKind.experts' }),
        value: ActivityActorKindEnum.experts
      }
    ],
    [intl]
  );

  const sectionOptions = useMemo(() => {
    const options = Object.values(ActivitySectionEnum).map(option => ({
      label: intl.formatMessage({ id: `models.activity.section.${option}` }),
      value: option as ActivitySectionEnum | ''
    }));
    options.unshift({ label: intl.formatMessage({ id: 'dictionary.all' }), value: '' });

    return options;
  }, [intl]);

  const expertOptions = useMemo(() => {
    const options = experts.map(expert => ({
      label: expert.fullName,
      value: expert.id
    }));

    options.unshift({ label: intl.formatMessage({ id: 'dictionary.all' }), value: '' });
    if (myExpert) {
      options.unshift({ label: intl.formatMessage({ id: 'dictionary.me' }), value: myExpert.id });
    }
    return options;
  }, [experts, intl, myExpert]);

  const clientRoleOptions = useMemo(() => {
    if (clientRoles) {
      const sortedClientOptions = clientRoles.map(clientRole => ({
        label: clientRole.client.fullName,
        value: clientRole.client.id
      }));
      sortedClientOptions.unshift({ label: intl.formatMessage({ id: 'dictionary.any' }), value: '' });
      return sortedClientOptions;
    }

    return [];
  }, [clientRoles, intl]);

  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    <Form hookForm={form} onSubmit={() => {}}>
      <HandleQuery query={getExpertsBasicQuery}>
        {_expertData => (
          <IonGrid className="ion-no-padding">
            <IonRow className="ion-align-items-center">
              <IonCol className="ion-margin-end">
                <Select<FormData>
                  control={form.control}
                  error={form.formState.errors.actorKind}
                  label={intl.formatMessage({ id: 'forms.activityFilters.actorKindLabel' })}
                  name="actorKind"
                  options={actorKindOptions}
                />
              </IonCol>
              <IonCol className={`${inDashboard ? 'ion-hide' : ''} ion-margin-end`}>
                <Select<FormData>
                  control={form.control}
                  error={form.formState.errors.startTime}
                  label={intl.formatMessage({ id: 'forms.activityFilters.startTimeLabel' })}
                  name="startTime"
                  options={startTimeOptions}
                />
              </IonCol>
              <IonCol
                className={`${
                  !inDashboard && myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager ? '' : 'ion-hide'
                } ion-margin-end`}
              >
                <Select<FormData>
                  control={form.control}
                  error={form.formState.errors.expertId}
                  label={intl.formatMessage({ id: 'forms.expertConsultsFilters.expertLabel' })}
                  name="expertId"
                  options={expertOptions}
                />
              </IonCol>
              <IonCol
                className={`${
                  inDashboard || (clientRoles && clientRoles.length === 1) ? 'ion-hide' : ''
                } ion-margin-end`}
              >
                {clientRoles ? (
                  <Select<FormData>
                    control={form.control}
                    error={form.formState.errors.clientId}
                    label={intl.formatMessage({ id: 'forms.activityFilters.clientIdLabel' })}
                    name="clientId"
                    options={clientRoleOptions}
                  />
                ) : (
                  <AsyncSelect<FormData>
                    control={form.control}
                    defaultOptions={expertClientsAsyncSearch.defaultOptions}
                    error={form.formState.errors.clientId}
                    label={intl.formatMessage({ id: 'forms.activityFilters.clientIdLabel' })}
                    name="clientId"
                    onSearch={async searchTerm =>
                      expertClientsAsyncSearch.searchOptions(
                        searchTerm,
                        getExpertClientsQuery({
                          variables: {
                            expertId:
                              expertId ??
                              (myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager ? '' : myExpert?.id ?? ''),
                            searchTerm
                          }
                        })
                      )
                    }
                    onSelection={(option: Option) => {
                      setQuery({ ...query, ...{ clientName: option.label } });
                      expertClientsAsyncSearch.setCurrentOption(option);
                    }}
                    value={expertClientsAsyncSearch.currentOption}
                  />
                )}
              </IonCol>
              <IonCol className={`${inDashboard ? 'ion-hide' : ''} ion-margin-end`}>
                <Select<FormData>
                  control={form.control}
                  error={form.formState.errors.section}
                  label={intl.formatMessage({ id: 'forms.activityFilters.sectionLabel' })}
                  name="section"
                  options={sectionOptions}
                />
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol>
                <RadioButtonGroup<FormData>
                  control={form.control}
                  error={form.formState.errors.activityKind}
                  name="activityKind"
                  options={activityKindOptions}
                />
              </IonCol>
            </IonRow>
          </IonGrid>
        )}
      </HandleQuery>
    </Form>
  );
};

export default ActivityFilters;
